import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QualificationsAPI } from "../api/qualifications";
import type {
  CreateFormRequest,
  QualificationForm,
  UpdateFormRequest,
} from "../types";
import { isDemoMode } from "@/features/utils/demo-mode";
import { mockForms } from "./use-qualification-forms.mock";

// Query keys
export const formKeys = {
  all: ["qualification_forms"] as const,
  forms: (clientId: string, options?: { status?: string }) =>
    [...formKeys.all, "forms", clientId, options] as const,
  form: (clientId: string, formId: string) =>
    [...formKeys.all, "form", clientId, formId] as const,
};

// Query hooks
export function useForms(options?: {
  status?: "active" | "inactive" | "draft";
}) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<QualificationForm[]>({
    queryKey: formKeys.forms(clientId, options),
    queryFn: () => {
      if (isDemoMode()) {
        let filtered = [...mockForms];
        if (options?.status) {
          filtered = filtered.filter((f) => f.status === options.status);
        }
        return Promise.resolve(filtered);
      }
      return QualificationsAPI.listForms(clientId, options);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

export function useForm(formId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<QualificationForm>({
    queryKey: formKeys.form(clientId, formId),
    queryFn: () => {
      if (isDemoMode()) {
        const form = mockForms.find((f) => f.id === formId);
        return Promise.resolve(form!);
      }
      return QualificationsAPI.getForm(clientId, formId);
    },
    enabled: !!clientId && !!formId,
    staleTime: 30 * 1000,
  });
}

// Mutation hooks
export function useCreateForm() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFormRequest) =>
      QualificationsAPI.createForm(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all });
    },
  });
}

export function useUpdateForm() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formId,
      data,
    }: {
      formId: string;
      data: UpdateFormRequest;
    }) => QualificationsAPI.updateForm(clientId, formId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all });
    },
  });
}

export function useDeleteForm() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formId: string) =>
      QualificationsAPI.deleteForm(clientId, formId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all });
    },
  });
}

export function useGenerateForm() {
  return useMutation({
    mutationFn: (params: { prompt: string; url?: string }) =>
      QualificationsAPI.generateForm(params.prompt, { url: params.url }),
  });
}
